import { useState } from 'react';
import {
  Camera,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  Droplets,
  Flame,
  Gauge,
  Lightbulb,
  Shield,
} from 'lucide-react';
import {
  INITIAL_CHECKLIST_STATE,
  PRE_TRIP_CHECKLIST,
} from './mockMobileData';

const CHECK_ICONS = {
  circle: Circle,
  gauge: Gauge,
  lightbulb: Lightbulb,
  droplets: Droplets,
  shield: Shield,
  flame: Flame,
};

function ChecklistItem({ item, checked, onToggle }) {
  const Icon = CHECK_ICONS[item.icon] || Circle;

  return (
    <div className="flex items-start gap-3 rounded-xl border border-logi-grid bg-white p-3 transition hover:border-logi-grid hover:bg-white">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onToggle}
        className={`relative mt-0.5 flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-emerald-600' : 'bg-logi-border'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Icon
            className={`h-4 w-4 flex-shrink-0 ${checked ? 'text-emerald-700' : 'text-logi-text0'}`}
            strokeWidth={1.75}
          />
          <p className={`text-sm font-semibold ${checked ? 'text-logi-text' : 'text-logi-muted'}`}>
            {item.label}
          </p>
          {checked && <CheckCircle2 className="ml-auto h-4 w-4 text-emerald-700" />}
        </div>
        <p className="mt-0.5 text-[11px] leading-snug text-logi-text0">{item.description}</p>
      </div>
    </div>
  );
}

export default function PreTripInspection({
  checklist = PRE_TRIP_CHECKLIST,
  initialState = INITIAL_CHECKLIST_STATE,
}) {
  const [checks, setChecks] = useState(initialState);
  const [photoUploaded, setPhotoUploaded] = useState(false);

  const completedCount = Object.values(checks).filter(Boolean).length;
  const allComplete = completedCount === checklist.length;

  const toggle = (id) => {
    setChecks((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-amber-700" strokeWidth={1.75} />
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-700/80">
              Module 6 · Pre-Trip Safety
            </p>
            <h3 className="text-lg font-bold text-logi-text">Vehicle Inspection Portal</h3>
          </div>
        </div>
        <div
          className={`rounded-xl px-3 py-2 text-center ring-1 ${
            allComplete
              ? 'bg-emerald-50 ring-emerald-500/30'
              : 'bg-logi-silver/80 ring-logi-grid'
          }`}
        >
          <p className="font-mono text-lg font-bold text-logi-text">
            {completedCount}/{checklist.length}
          </p>
          <p className="text-[10px] text-logi-text0">Checks passed</p>
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-logi-grid bg-white px-4 py-3 text-sm text-logi-muted">
        Vehicle: <span className="font-mono font-semibold text-logi-accent">DXB-T-4021</span>
        {' · '}
        Driver: <span className="text-logi-text">Ahmed Al Mansoori</span>
        {' · '}
        Trip: <span className="font-mono text-logi-muted">TRP-2026-8842</span>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto">
        {checklist.map((item) => (
          <ChecklistItem
            key={item.id}
            item={item}
            checked={checks[item.id]}
            onToggle={() => toggle(item.id)}
          />
        ))}
      </div>

      {/* Photo upload */}
      <div className="mt-4 rounded-xl border border-dashed border-logi-grid bg-white p-4">
        <button
          type="button"
          onClick={() => setPhotoUploaded(true)}
          className={`flex w-full flex-col items-center gap-2 rounded-lg py-4 transition ${
            photoUploaded
              ? 'bg-emerald-50 text-emerald-700'
              : 'hover:bg-logi-silver/80 text-logi-muted'
          }`}
        >
          <Camera className="h-8 w-8" strokeWidth={1.25} />
          <span className="text-sm font-semibold">
            {photoUploaded ? 'Pre-Trip Photo Captured ✓' : 'Driver Pre-Trip Vehicle Photo Upload'}
          </span>
          <span className="text-[10px] text-logi-text0">
            {photoUploaded ? 'IMG_8842_ruwais_depot.jpg' : 'Tap to simulate camera capture'}
          </span>
        </button>
      </div>

      <button
        type="button"
        disabled={!allComplete || !photoUploaded}
        className={`mt-4 w-full rounded-xl py-3 text-sm font-bold transition ${
          allComplete && photoUploaded
            ? 'bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-lg shadow-emerald-500/20'
            : 'cursor-not-allowed bg-white text-logi-text0 ring-1 ring-logi-grid'
        }`}
      >
        {allComplete && photoUploaded
          ? 'Authorize Trip Departure'
          : `Complete checklist to authorize (${completedCount}/${checklist.length})`}
      </button>
    </div>
  );
}
